import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Row, Col, Card, Form, Button, InputGroup, FormControl, Spinner, ProgressBar } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { ChargingBASE_URL } from '../../Config';

import { OverlayTrigger, Tooltip } from "react-bootstrap";

const columns = [
  {
    name: "Agent Name",
    selector: (row) => row.agentName,
    sortable: true,
  },
  {
    name: "Timestamp",
    selector: (row) => row.timestamp,
    sortable: true,
  },
  {
    name: "Reason",
    selector: (row) => row.reason,
    sortable: true,
    cell: (row) => (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-reason-${row.transactionId}`}>
            {row.reason || "No Reason Provided"}
          </Tooltip>
        }
      >
        <span
          className="text-truncate"
          style={{
            display: "inline-block",
            maxWidth: "150px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer"
          }}
        >
          {row.reason}
        </span>
      </OverlayTrigger>
    ),
  },
  {
    name: "Service",
    selector: (row) => row.service,
    sortable: true,
  },
  {
    name: "Amount",
    selector: (row) => row.amount,
    sortable: true,
  },
  {
    name: "Customer Account Number",
    selector: (row) => row.account_Number,
    sortable: true,
  },
  {
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
  },
  {
    name: "Transaction ID",
    selector: (row) => row.transactionId,
    sortable: true,
  },
  {
    name: "Digital Consent",
    selector: (row) => row.digital_Consent,
    sortable: true,
  },

];


const PartnerReportbyDateFilter = () => {
    const [data, setData] = useState([]); // State for storing dynamic data
    const [filteredData, setFilteredData] = useState([]); // State for storing filtered data
    const [isLoading, setIsLoading] = useState(false); // Loading state
    const [progress, setProgress] = useState(1); // State for progress bar
    const [error, setError] = useState(null); // Error state
    const [startDate, setStartDate] = useState(""); // State for start date
    const [endDate, setEndDate] = useState(""); // State for end date (nullable)
    const [searchTerm, setSearchTerm] = useState(""); // State for search term
    const [userId, setUserId] = useState(""); 
    
    useEffect(() => {
        const user = JSON.parse(sessionStorage.getItem("user"));
        if (user) {
            setUserId(user.userId); // Save UserId from session to state
        }
    }, []);

    // Fetch data when the user submits the form
    const fetchData = async () => {
        if (!startDate) {
            setError("Start Date is required");
            return;
        }

        const url = new URL(`${ChargingBASE_URL}JazzcashReportByDateRange`);
        const params = {
            UserId: userId,
            StartDate: startDate,
            EndDate: endDate || "",
        };

        url.search = new URLSearchParams(params).toString();

        try {
            setIsLoading(true);
            setProgress(1); // Start progress at 1%
            // Interval to simulate progress from 1% to 100%
            const progressInterval = setInterval(() => {
                setProgress((prevProgress) => {
                    if (prevProgress >= 100) {
                        clearInterval(progressInterval); // Clear interval when progress reaches 100
                        return 100;
                    }
                    return prevProgress + 2; // Increase progress
                });
            }, 100); // Increase progress every 100ms

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Accept: "*/*"
                }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch data.");
            }

            const result = await response.json();
            setData(result);
            setFilteredData(result); 
            setError(null);
        } catch (error) {
            setError(error.message); 
        } finally {
            setIsLoading(false); 
        }
    };

    // Function to filter data based on search term
    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        const filtered = data.filter((row) =>
            Object.values(row).some((value) =>
                String(value).toLowerCase().includes(term)
            )
        );
        setFilteredData(filtered); // Update filtered data based on search term
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        fetchData(); // Call fetchData on form submission
    };

    return (
        <>
            {/* Show loading progress bar if data is being fetched */}
            {isLoading && (
                <div className="text-center mt-2">
                    <div className="blur-overlay" style={{ position: 'relative' }}>
                        <ProgressBar
                            animated
                            now={progress}
                            label={`Loading Data... ${progress}%`}
                            style={{ width: '100%' }} 
                        />
                    </div>
                </div>
            )}

            {/* Error handling */}
            {error && <div className="text-center mt-2">{error}</div>}

            <Row className="justify-content-center">
                <Col lg={10} md={12} sm={12}>
                    <Card className="mt-2 shadow-lg">
                        <Card.Body>
                            {/* Form for date range selection */}
                            <Form onSubmit={handleSubmit}>
                                <Row className="mb-3">
                                    <Col md={3} sm={6}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label>Start Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} sm={6}>
                                        <Form.Group controlId="endDate">
                                            <Form.Label>End Date (Optional)</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} sm={6}>
                                        <Button variant="primary" type="submit" className="mt-4 w-100" disabled={isLoading}>
                                            {isLoading ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                "Search Record"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                            <br />
                            {/* Conditionally render the search bar only when data has been loaded */}
                            {!isLoading && (
                                <InputGroup className="mb-3">
                                    <FormControl
                                        placeholder="Search records"
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                </InputGroup>
                            )}

                            {/* DataTable for displaying the fetched data */}
                            <DataTable
                                columns={columns}
                                data={filteredData} // Pass filtered data to the DataTable
                                pagination
                                responsive
                                highlightOnHover
                                striped
                                dense
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 15, 20, 50]}
                                defaultSortFieldId={1} // Default sort column (Timestamp)
                                defaultSortAsc={true} // Default sort order (ascending)
                            />
                            {/* Conditionally render the Download Button */}
                            {filteredData.length > 0 && !isLoading && (
                                <CSVLink
                                    data={filteredData}
                                    filename={"transaction_data.csv"}
                                    className="btn btn-success mt-1 w-100"
                                >
                                    Download CSV
                                </CSVLink>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default PartnerReportbyDateFilter;
